import React, { useEffect } from 'react';
import { useStore } from 'react-redux';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';

import { TooltipIconButton } from '@paragon/client-tools';
import { hasAssistantFeatureFlag } from '@paragon/login-tools';

import { assistantStore, setAssistantOpen, useAssistantState } from '../../assistant/assistantStore';
import { ASSISTANT_TOOLS } from '../../assistant/tools';
import { publishToolsToAgents } from '../../assistant/webMcp';
import type { RootState } from '../../store';
import { useAppDispatch, useAppSelector } from '../hooks';
import { AssistantPanel } from './AssistantPanel';

/**
 * The Assistant's chat windows. Each keeps its own draft and attachments, so a window is keyed by its chat.
 */
function AssistantWindows() {
  const chatIds = useAssistantState((state) => state.chats.map((chat) => chat.id).join(' '));

  return (
    <>
      {chatIds.split(' ').map((chatId, position) => (
        <AssistantPanel key={chatId} chatId={chatId} position={position} />
      ))}
    </>
  );
}

/**
 * The Assistant's chat windows, and the same tools published to Agents, all behind the feature flag.
 */
export function AssistantRoot() {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const enabled = useAppSelector(hasAssistantFeatureFlag);

  useEffect(() => {
    if (!enabled) return;
    const unpublish = publishToolsToAgents(ASSISTANT_TOOLS, dispatch, store.getState);
    return () => unpublish?.();
  }, [enabled, dispatch, store]);

  if (!enabled) return null;

  return (
    <>
      <AssistantWindows />
    </>
  );
}

export function AssistantHeaderButton({ sx }: { sx?: React.ComponentProps<typeof TooltipIconButton>['sx'] }) {
  const enabled = useAppSelector(hasAssistantFeatureFlag);
  if (!enabled) return null;

  return (
    <TooltipIconButton title="Assistant" sx={sx} onClick={() => setAssistantOpen(!assistantStore.getState().open)}>
      <AutoAwesomeIcon />
    </TooltipIconButton>
  );
}

/**
 * The Assistant in the header's menu, for windows too narrow for its button.
 */
export function AssistantMenuItem() {
  const enabled = useAppSelector(hasAssistantFeatureFlag);
  if (!enabled) return null;

  return (
    <MenuItem onClick={() => setAssistantOpen(true)}>
      <ListItemIcon>
        <AutoAwesomeIcon />
      </ListItemIcon>
      <ListItemText>Assistant</ListItemText>
    </MenuItem>
  );
}
